import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, LogOut, Download, FileSpreadsheet, Wifi, WifiOff, Trophy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimingRecords = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [stages, setStages] = useState([]); // For stage lookup
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPhotoRecord, setSelectedPhotoRecord] = useState(null);
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoCount, setPhotoCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isCurrentEventOnly, setIsCurrentEventOnly] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchRecords();
    fetchStages();
    
    // Auto-refresh data every 10 seconds
    const refreshInterval = setInterval(() => {
      if (selectedEvent) {
        fetchRecords(selectedEvent.id);
      } else {
        fetchRecords();
      }
    }, 10000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${API}/stages`, getAuthHeaders());
      setStages(response.data);
    } catch (error) {
      console.error('Failed to fetch stages');
    }
  };

  // Helper to get stage display info
  const getStageDisplay = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return { label: '-', name: 'Unknown' };
    if (stage.stage_type === 'stage') return { label: `SS${stage.stage_number}`, name: stage.name };
    if (stage.stage_type === 'service_park') return { label: 'Service', name: stage.name };
    if (stage.stage_type === 'rally_start') return { label: 'Section', name: stage.name };
    return { label: '-', name: stage.name };
  };

  useEffect(() => {
    if (selectedEvent) {
      fetchRecords(selectedEvent.id);
    }
  }, [selectedEvent]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchEvents = async () => {
    try {
      const userRole = user.active_role || user.role;
      
      // If user is scorer or marshal, only fetch current event
      if (userRole === 'scorer' || userRole === 'marshal') {
        const response = await axios.get(`${API}/events/current`, getAuthHeaders());
        if (response.data) {
          setEvents([response.data]);
          setSelectedEvent(response.data);
          setIsCurrentEventOnly(true);
          // Fetch records for current event
          fetchRecords(response.data.id);
        } else {
          toast.error('No current event set. Please contact administrator.');
        }
      } else {
        // Admin gets all events
        const response = await axios.get(`${API}/events`, getAuthHeaders());
        setEvents(response.data);
        setIsCurrentEventOnly(false);
      }
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  const fetchRecords = async (eventId = null) => {
    setLoading(true);
    try {
      const url = eventId ? `${API}/timing-records?event_id=${eventId}&limit=500` : `${API}/timing-records?limit=500`;
      const response = await axios.get(url, getAuthHeaders());
      setRecords(response.data);
      // Count photos
      const count = response.data.filter(r => r.photo_data && r.photo_data.startsWith('data:image')).length;
      setPhotoCount(count);
    } catch (error) {
      toast.error('Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const url = selectedEvent ? `${API}/timing-records/export/excel?event_id=${selectedEvent.id}` : `${API}/timing-records/export/excel`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `vosloor_timing_records_${Date.now()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const exportPhotos = async () => {
    try {
      const url = selectedEvent ? `${API}/timing-records/export/photos?event_id=${selectedEvent.id}` : `${API}/timing-records/export/photos`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `vosloor_timing_photos_${Date.now()}.zip`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Photos ZIP file downloaded successfully');
    } catch (error) {
      toast.error('Failed to export photos');
    }
  };

  const groupRecordsByCar = () => {
    const grouped = {};
    records.forEach(record => {
      if (!grouped[record.car_number]) {
        grouped[record.car_number] = [];
      }
      grouped[record.car_number].push(record);
    });
    return grouped;
  };

  const groupedRecords = groupRecordsByCar();

  return (
    <div className="min-h-screen bg-[#09090b] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">TIMING RECORDS</h1>
            <p className="text-zinc-400 mono-text text-xs sm:text-sm mt-1">{user.name} - {user.role}</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Online Status Indicator */}
            <div className="flex items-center gap-1">
              {isOnline ? (
                <div className="flex items-center gap-1 text-green-500">
                  <Wifi size={16} />
                  <span className="hidden sm:inline text-sm">Online</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-500">
                  <WifiOff size={16} />
                  <span className="hidden sm:inline text-sm">Offline</span>
                </div>
              )}
            </div>
            <NotificationBell user={user} />
            <Button
              onClick={() => navigate('/live-results')}
              className="bg-[#facc15] text-black hover:bg-[#eab308] text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4 font-bold"
              data-testid="live-results-button"
            >
              <Trophy className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">LIVE TIMING</span>
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="border-[#27272a] text-zinc-300 hover:bg-[#27272a] text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4"
              data-testid="records-logout-button"
            >
              <LogOut className="sm:mr-2" size={16} />
              <span className="hidden sm:inline">LOGOUT</span>
            </Button>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              {isCurrentEventOnly ? (
                <div className="w-full bg-[#27272a] border border-[#27272a] text-white rounded p-2 text-sm">
                  <span className="text-[#facc15] font-bold">CURRENT EVENT:</span> {selectedEvent?.name || 'No event set'}
                </div>
              ) : (
                <select
                  value={selectedEvent?.id || ''}
                  onChange={(e) => {
                    const event = events.find(ev => ev.id === e.target.value);
                    setSelectedEvent(event || null);
                  }}
                  className="w-full bg-[#27272a] border-[#27272a] text-white rounded p-2 text-sm"
                  data-testid="event-filter-select"
                >
                  <option value="">All Events</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>{event.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="grid grid-cols-3 sm:flex gap-2">
              <Button
                onClick={() => fetchRecords(selectedEvent?.id)}
                disabled={loading}
                className="bg-[#27272a] text-white hover:bg-[#3f3f46] h-10 text-xs sm:text-sm px-2 sm:px-4"
                data-testid="refresh-button"
              >
                <RefreshCw className={`sm:mr-2 ${loading ? 'animate-spin' : ''}`} size={16} />
                <span className="hidden sm:inline">REFRESH</span>
              </Button>
              <Button
                onClick={exportData}
                className="bg-[#22c55e] text-white hover:bg-[#16a34a] h-10 text-xs sm:text-sm px-2 sm:px-4"
                data-testid="export-button"
              >
                <FileSpreadsheet className="sm:mr-2" size={16} />
                <span className="hidden sm:inline">EXCEL</span>
              </Button>
              <Button
                onClick={exportPhotos}
                className="bg-[#3b82f6] text-white hover:bg-[#2563eb] h-10 text-xs sm:text-sm px-2 sm:px-4"
                data-testid="export-photos-button"
              >
                <Download className="sm:mr-2" size={16} />
                <span className="hidden sm:inline">PHOTOS ({photoCount})</span>
                <span className="sm:hidden">({photoCount})</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Records Display */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-3 sm:p-6">
          <h2 className="text-lg sm:text-xl font-black uppercase-label text-white mb-4">
            {records.length} RECORDS
          </h2>

          {Object.keys(groupedRecords).length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-400">No timing records found</p>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {Object.entries(groupedRecords).map(([carNumber, carRecords]) => (
                <div key={carNumber} className="border border-[#27272a] rounded-lg p-3 sm:p-4 card-strip-yellow">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl sm:text-2xl font-black text-[#facc15] mono-text">CAR #{carNumber}</h3>
                    <span className="text-xs sm:text-sm text-zinc-400">{carRecords.length} records</span>
                  </div>

                  {/* Mobile-friendly card view */}
                  <div className="block sm:hidden space-y-2">
                    {carRecords.map(record => {
                      const stageInfo = getStageDisplay(record.stage_id);
                      return (
                      <div key={record.id} className="bg-[#27272a]/50 rounded p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="text-xs font-bold text-[#facc15] mr-2">{stageInfo.label}</span>
                            <span className="text-xs text-zinc-400">- {stageInfo.name}</span>
                            <div className="text-xs uppercase font-bold text-zinc-300 mt-1">
                              {record.timing_type.replace('_', ' ')}
                            </div>
                          </div>
                          {record.photo_data && (
                            <img
                              src={record.photo_data}
                              alt="Car"
                              className="w-12 h-9 object-cover rounded cursor-pointer"
                              onClick={() => {
                                setSelectedPhotoRecord(record);
                                setShowPhotoDialog(true);
                              }}
                            />
                          )}
                        </div>
                        <div className="text-sm text-white mono-text">
                          {new Date(record.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date(record.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">{record.recorded_by_name}</div>
                        {record.description && (
                          <div className="text-xs text-zinc-400 mt-2 italic">"{record.description}"</div>
                        )}
                      </div>
                    );
                    })}
                  </div>

                  {/* Desktop table view */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#27272a]">
                          <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Stage</th>
                          <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Type</th>
                          <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Timestamp</th>
                          <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Recorded By</th>
                          <th className="text-left py-2 px-3 text-zinc-400 uppercase-label text-xs">Photo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {carRecords.map(record => {
                          const stageInfo = getStageDisplay(record.stage_id);
                          return (
                          <tr key={record.id} className="border-b border-[#27272a]/30">
                            <td className="py-2 px-3">
                              <div>
                                <span className="text-xs font-bold text-[#facc15]">{stageInfo.label}</span>
                                <span className="text-xs text-zinc-400 ml-1">- {stageInfo.name}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3">
                              <span className="text-xs uppercase-label text-zinc-300">
                                {record.timing_type.replace('_', ' ')}
                              </span>
                              {record.description && (
                                <div className="text-xs text-zinc-500 italic mt-1">"{record.description}"</div>
                              )}
                            </td>
                            <td className="py-2 px-3 text-white mono-text text-sm">
                              {new Date(record.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} {new Date(record.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </td>
                            <td className="py-2 px-3 text-zinc-400 text-sm">{record.recorded_by_name}</td>
                            <td className="py-2 px-3">
                              {record.photo_data ? (
                                <img
                                  src={record.photo_data}
                                  alt="Car"
                                  className="w-16 h-12 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => {
                                    setSelectedPhotoRecord(record);
                                    setShowPhotoDialog(true);
                                  }}
                                  data-testid={`photo-thumbnail-${record.id}`}
                                />
                              ) : (
                                <span className="text-xs text-zinc-500">No photo</span>
                              )}
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="max-w-4xl bg-[#18181b] border-[#27272a]">
          <DialogHeader>
            <DialogTitle className="text-white font-black uppercase-label">
              CAR #{selectedPhotoRecord?.car_number} - {selectedPhotoRecord?.timing_type?.replace('_', ' ')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPhotoRecord?.photo_data && (
              <div className="flex justify-center">
                <img
                  src={selectedPhotoRecord.photo_data}
                  alt={`Car ${selectedPhotoRecord.car_number}`}
                  className="max-w-full max-h-[70vh] object-contain rounded"
                  data-testid="photo-dialog-image"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-400 uppercase-label text-xs">Timestamp:</span>
                <p className="text-white mono-text">
                  {selectedPhotoRecord && `${new Date(selectedPhotoRecord.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${new Date(selectedPhotoRecord.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
                </p>
              </div>
              <div>
                <span className="text-zinc-400 uppercase-label text-xs">Recorded By:</span>
                <p className="text-white">{selectedPhotoRecord?.recorded_by_name}</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimingRecords;